import React, { useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faXmark, faRotateLeft, faPen, faGift, faHourglassHalf } from '@fortawesome/free-solid-svg-icons';
import './EarningsBonuses.css';

/* =========================================================
   Admin › Earnings › "Package bonuses" tab (the month shown on the page).
   - Waiting for your OK: new students from a demo. Nothing is paid before you confirm.
   - Counted: in the teachers' pay this month (remove, or change the amount).
   - Not counted: removed, or no demo (added by hand / an old account reused). "Give bonus" adds it.
   Same calls as the teacher profile's Earnings tab (/api/teacher/earnings/bonus-*: admin only).
   ========================================================= */
const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });
const money = (n) => (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const day = (v) => {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};
const SOURCE = { demo: 'from their demo', 'first class': 'first class (demo not found)', 'no demo': 'no demo', class: 'their teacher' };

const EarningsBonuses = ({ items = [], enabled, onChanged }) => {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');

  const call = async (b, path, body) => {
    setBusy(b.paymentId);
    setError('');
    try {
      await axios.post(`${API}/api/teacher/earnings/${path}`, body, auth());
      onChanged();
    } catch (err) {
      setError(err.response?.data?.error || 'Couldn’t save. Try again.');
    } finally {
      setBusy(null);
    }
  };
  const give = (b) => call(b, 'bonus-exclude', { IdPayment: b.paymentId, exclude: false });
  const remove = (b) => call(b, 'bonus-exclude', { IdPayment: b.paymentId, exclude: true, Reason: 'Removed by admin' });
  const amount = (b) => {
    const v = window.prompt(`Bonus for ${b.student} (${b.teacher}). Empty = back to the normal amount.`, String(b.amount));
    if (v === null) return;
    const clean = v.trim() === '' ? null : Number(v);
    if (clean !== null && (!Number.isFinite(clean) || clean < 0)) return;
    call(b, 'bonus-amount', { IdPayment: b.paymentId, Amount: clean });
  };

  if (!enabled && !items.length) {
    return <p className="rmaebn-empty">Package bonuses are turned off (Prices & Offers › Teacher package bonus).</p>;
  }

  const waiting = items.filter((b) => b.waiting);
  const counted = items.filter((b) => !b.excluded);
  const off = items.filter((b) => b.excluded && !b.waiting);
  const total = counted.reduce((s, b) => s + (Number(b.amount) || 0), 0);

  const row = (b, actions) => (
    <li key={b.paymentId} className={`rmaebn-row ${b.waiting ? 'is-wait' : b.excluded ? 'is-off' : ''}`}>
      <span className="rmaebn-main">
        <strong>
          <a href={`/admin/StudentProfile/${b.studentId}`} target="_blank" rel="noopener noreferrer">
            {b.student}
          </a>
          <span className="rmaebn-arrow">→</span> {b.teacher}
        </strong>
        <small>
          {b.kind === 'new' ? `New student · ${SOURCE[b.source] || b.source}` : 'Renewal'} · {b.sessions} {b.type === 'mastery' ? 'Mastery' : 'Standard'} sessions · {day(b.paidAt)}
          {!b.paid && ' · not paid yet'}
        </small>
        {b.reason && !b.waiting && <em>{b.reason}</em>}
        {b.custom && !b.excluded && <em>Amount changed</em>}
      </span>
      <span className="rmaebn-amount">{b.excluded ? <s>{money(b.amount)}</s> : money(b.amount)}</span>
      <span className="rmaebn-actions">{actions}</span>
    </li>
  );

  return (
    <div className="rmaebn">
      {error && <p className="rmaebn-error">{error}</p>}

      <section>
        <h3>
          <FontAwesomeIcon icon={faHourglassHalf} /> Waiting for your OK <span>{waiting.length}</span>
        </h3>
        {waiting.length === 0 ? (
          <p className="rmaebn-none">Nothing to confirm. New students from a demo show here; no bonus is paid before you confirm it.</p>
        ) : (
          <ul>
            {waiting.map((b) =>
              row(
                b,
                <>
                  <button type="button" className="rmaebn-btn" disabled={busy === b.paymentId} onClick={() => give(b)}>
                    <FontAwesomeIcon icon={faCheck} /> Confirm
                  </button>
                  <button type="button" className="rmaebn-btn rmaebn-btn--ghost" disabled={busy === b.paymentId} onClick={() => remove(b)}>
                    <FontAwesomeIcon icon={faXmark} /> No bonus
                  </button>
                </>
              )
            )}
          </ul>
        )}
      </section>

      <section>
        <h3>
          <FontAwesomeIcon icon={faGift} /> Counted this month <span>{counted.length}</span>
          <b className="rmaebn-total">{money(total)}</b>
        </h3>
        {counted.length === 0 ? (
          <p className="rmaebn-none">No bonuses in the teachers’ pay this month.</p>
        ) : (
          <ul>
            {counted.map((b) =>
              row(
                b,
                <>
                  <button type="button" className="rmaebn-icon" disabled={busy === b.paymentId} onClick={() => amount(b)} title="Change the amount" aria-label={`Change the bonus amount for ${b.student}`}>
                    <FontAwesomeIcon icon={faPen} />
                  </button>
                  <button type="button" className="rmaebn-icon is-danger" disabled={busy === b.paymentId} onClick={() => remove(b)} title="Remove" aria-label={`Remove the bonus for ${b.student}`}>
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                </>
              )
            )}
          </ul>
        )}
      </section>

      {off.length > 0 && (
        <section>
          <h3>
            Not counted <span>{off.length}</span>
          </h3>
          <ul>
            {off.map((b) =>
              row(
                b,
                <button type="button" className="rmaebn-btn rmaebn-btn--ghost" disabled={busy === b.paymentId} onClick={() => give(b)}>
                  <FontAwesomeIcon icon={faRotateLeft} /> Give bonus
                </button>
              )
            )}
          </ul>
        </section>
      )}
    </div>
  );
};

export default EarningsBonuses;
